/**
 * TutorialTooltip Component (AC-670)
 *
 * Positioned tooltip showing step instructions during the tutorial.
 * Displays step content, progress indicator, and navigation buttons.
 *
 * Technical Design Reference: docs/technical/TDD-AC-661-tutorial-system.md
 *
 * Features:
 * - Step title and description
 * - Action prompt
 * - Progress indicator (Step X of Y) with progress bar
 * - Skip and Exit buttons
 * - Smart positioning relative to target element
 * - Arrow pointing to target
 * - Smooth transitions when repositioning
 * - Accessible with proper ARIA attributes
 *
 * @see AC-670 - TutorialTooltip component
 * @see AC-661 - Epic: Interactive Tutorial System
 */

import React, { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Check } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import { TutorialTooltipProps } from '../../types/tutorial';
import { useThemeStore } from '../../store/themeStore';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * Copyable text component with copy button.
 * Shows a copy icon that changes to a checkmark briefly after copying.
 */
const CopyableText: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Use fallback method for insecure contexts (HTTP over IP address)
    // The modern clipboard API requires HTTPS or localhost
    const useModernAPI = window.isSecureContext && navigator.clipboard?.writeText;

    const copyWithFallback = () => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textArea);
      }
    };

    try {
      if (useModernAPI) {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => {
          // Fallback if clipboard API fails
          copyWithFallback();
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      } else {
        // Use fallback for insecure contexts (HTTP)
        copyWithFallback();
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
      // Still show feedback even on error so user knows click registered
      setCopied(true);
      setTimeout(() => setCopied(false), 500);
    }
  }, [text]);

  return (
    <span className="inline-flex items-center gap-1 font-semibold text-blue-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 px-1.5 py-0.5 rounded group/copy">
      <span>{text}</span>
      <Tooltip content="Click to copy">
      <button
        type="button"
        onClick={handleCopy}
        className="relative inline-flex items-center justify-center w-5 h-5 ml-0.5 text-primary-500 hover:text-primary-700 hover:bg-primary-100 focus:outline-none focus:ring-1 focus:ring-primary-500 rounded transition-all after:absolute after:inset-[-14px] after:content-[''] md:after:inset-0"
        aria-label={`Copy "${text}" to clipboard`}
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-600" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </button>
      </Tooltip>
    </span>
  );
};

/**
 * Compact copy button that copies hidden text to clipboard.
 * Used for large blocks (e.g., agent instructions) that shouldn't be displayed inline.
 */
const CopyButton: React.FC<{ label: string; text: string }> = ({ label, text }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    const copy = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    };
    try {
      if (isSecure && navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => { copy(); setCopied(true); setTimeout(() => setCopied(false), 1500); });
      } else {
        copy();
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch { setCopied(true); setTimeout(() => setCopied(false), 500); }
  }, [text]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 my-1 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
    >
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
      {copied ? 'Copied!' : label}
    </button>
  );
};

/**
 * Non-copyable bold text component for UI element references.
 * Used for buttons, icons, and navigation elements that shouldn't have copy functionality.
 */
const BoldText: React.FC<{ text: string }> = ({ text }) => (
  <span className="font-semibold text-gray-900 dark:text-warm-white">{text}</span>
);

/**
 * Parse simple markdown-style formatting in text.
 * Supports two types of bold syntax:
 * - **text** - Copyable bold text for form values users should enter
 * - __text__ - Non-copyable bold text for UI element references (buttons, icons, etc.)
 */
function parseFormattedText(text: string): React.ReactNode {
  // First extract [[copy:label:text]] blocks (large copyable content rendered as buttons)
  const copyBlockRegex = /\[\[copy:([^:]+):([^\]]*(?:\](?!\])[^\]]*)*)\]\]/g;
  const copyBlocks: { start: number; end: number; label: string; content: string }[] = [];
  let match;
  while ((match = copyBlockRegex.exec(text)) !== null) {
    copyBlocks.push({ start: match.index, end: match.index + match[0].length, label: match[1], content: match[2] });
  }

  // If there are copy blocks, split text around them and process each segment
  if (copyBlocks.length > 0) {
    const result: React.ReactNode[] = [];
    let lastEnd = 0;
    copyBlocks.forEach((block, i) => {
      if (block.start > lastEnd) {
        result.push(<React.Fragment key={`t${i}`}>{parseInlineFormatting(text.slice(lastEnd, block.start))}</React.Fragment>);
      }
      result.push(<CopyButton key={`cb${i}`} label={block.label} text={block.content} />);
      lastEnd = block.end;
    });
    if (lastEnd < text.length) {
      result.push(<React.Fragment key="tail">{parseInlineFormatting(text.slice(lastEnd))}</React.Fragment>);
    }
    return result;
  }

  return parseInlineFormatting(text);
}

/**
 * Parse inline **bold** (copyable) and __bold__ (non-copyable) markers.
 */
function parseInlineFormatting(text: string): React.ReactNode {
  // Split by both **bold** (copyable) and __bold__ (non-copyable) markers
  // The __...__ pattern uses a non-greedy match that allows single underscores inside
  const parts = text.split(/(\*\*[^*]+\*\*|__(?:(?!__).)+__)/g);

  return parts.map((part, index) => {
    // Check if this part is wrapped in ** (copyable)
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <CopyableText key={index} text={boldText} />;
    }
    // Check if this part is wrapped in __ (non-copyable, for UI elements)
    if (part.startsWith('__') && part.endsWith('__')) {
      const boldText = part.slice(2, -2);
      return <BoldText key={index} text={boldText} />;
    }
    return part;
  });
}

/**
 * Gap between tooltip and target element (in pixels).
 */
const TOOLTIP_GAP = 12;

/**
 * Max width of tooltip (in pixels).
 */
const TOOLTIP_MAX_WIDTH = 320;

/**
 * Min width of tooltip (in pixels).
 */
const TOOLTIP_MIN_WIDTH = 280;

/**
 * Calculate tooltip position based on target rect and preferred position.
 */
function calculatePosition(
  targetRect: DOMRect | null,
  position: 'top' | 'bottom' | 'left' | 'right' | 'auto' | 'screen-right',
  tooltipWidth: number = TOOLTIP_MAX_WIDTH,
  tooltipHeight: number = 200, // Estimated height
  isMobile: boolean = false
): { top: number; left: number; actualPosition: 'top' | 'bottom' | 'left' | 'right' } {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Special case: screen-right positions tooltip on right edge of viewport
  // This keeps the tooltip out of the main content area (useful for editors)
  if (position === 'screen-right') {
    // On mobile, center tooltip at bottom instead of right edge
    if (isMobile) {
      return {
        top: viewportHeight - tooltipHeight - TOOLTIP_GAP,
        left: (viewportWidth - tooltipWidth) / 2,
        actualPosition: 'bottom',
      };
    }
    return {
      top: Math.max(TOOLTIP_GAP, (viewportHeight - tooltipHeight) / 3), // Upper third of screen
      left: viewportWidth - tooltipWidth - TOOLTIP_GAP,
      actualPosition: 'right',
    };
  }

  if (!targetRect) {
    // Center on screen if no target
    return {
      top: window.innerHeight / 2 - tooltipHeight / 2,
      left: window.innerWidth / 2 - tooltipWidth / 2,
      actualPosition: 'bottom',
    };
  }

  // Calculate available space in each direction
  const spaceTop = targetRect.top;
  const spaceBottom = viewportHeight - targetRect.bottom;
  const spaceLeft = targetRect.left;
  const spaceRight = viewportWidth - targetRect.right;

  // Determine actual position (auto calculates best fit)
  let actualPosition: 'top' | 'bottom' | 'left' | 'right';
  if (position === 'auto') {
    const spaces = [
      { pos: 'right' as const, space: spaceRight },
      { pos: 'bottom' as const, space: spaceBottom },
      { pos: 'left' as const, space: spaceLeft },
      { pos: 'top' as const, space: spaceTop },
    ];
    actualPosition = spaces.sort((a, b) => b.space - a.space)[0].pos;
  } else {
    actualPosition = position;
  }

  let top = 0;
  let left = 0;

  switch (actualPosition) {
    case 'top':
      top = targetRect.top - tooltipHeight - TOOLTIP_GAP;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      break;
    case 'bottom':
      top = targetRect.bottom + TOOLTIP_GAP;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      break;
    case 'left':
      // Align tooltip top with target top (not centered) to avoid overlapping dropdowns below
      top = targetRect.top;
      left = targetRect.left - tooltipWidth - TOOLTIP_GAP;
      break;
    case 'right':
      // Align tooltip top with target top (not centered) to avoid overlapping dropdowns below
      top = targetRect.top;
      left = targetRect.right + TOOLTIP_GAP;
      break;
  }

  // Ensure tooltip stays within viewport
  left = Math.max(TOOLTIP_GAP, Math.min(left, viewportWidth - tooltipWidth - TOOLTIP_GAP));
  top = Math.max(TOOLTIP_GAP, Math.min(top, viewportHeight - tooltipHeight - TOOLTIP_GAP));

  return { top, left, actualPosition };
}

/**
 * Get arrow styles based on position.
 */
function getArrowStyles(position: 'top' | 'bottom' | 'left' | 'right', isDark: boolean): React.CSSProperties {
  const color = isDark ? '#1A1A1A' : 'white'; // cap-dark in dark mode, white in light
  const baseStyles: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  };

  switch (position) {
    case 'top':
      // Arrow points down (tooltip above target)
      return {
        ...baseStyles,
        bottom: -8,
        left: '50%',
        transform: 'translateX(-50%)',
        borderWidth: '8px 8px 0 8px',
        borderColor: `${color} transparent transparent transparent`,
      };
    case 'bottom':
      // Arrow points up (tooltip below target)
      return {
        ...baseStyles,
        top: -8,
        left: '50%',
        transform: 'translateX(-50%)',
        borderWidth: '0 8px 8px 8px',
        borderColor: `transparent transparent ${color} transparent`,
      };
    case 'left':
      // Arrow points right (tooltip left of target)
      return {
        ...baseStyles,
        right: -8,
        top: '50%',
        transform: 'translateY(-50%)',
        borderWidth: '8px 0 8px 8px',
        borderColor: `transparent transparent transparent ${color}`,
      };
    case 'right':
      // Arrow points left (tooltip right of target)
      return {
        ...baseStyles,
        left: -8,
        top: '50%',
        transform: 'translateY(-50%)',
        borderWidth: '8px 8px 8px 0',
        borderColor: `transparent ${color} transparent transparent`,
      };
  }
}

/**
 * Get arrow CSS class for direction.
 */
function getArrowClass(position: 'top' | 'bottom' | 'left' | 'right'): string {
  switch (position) {
    case 'top':
      return 'arrow-down';
    case 'bottom':
      return 'arrow-up';
    case 'left':
      return 'arrow-right';
    case 'right':
      return 'arrow-left';
  }
}

/**
 * TutorialTooltip - Step instruction tooltip component.
 */
const TutorialTooltip: React.FC<TutorialTooltipProps> = ({
  step,
  stepNumber,
  totalSteps,
  targetRect,
  onSkip,
  onExit,
  onContinue,
}) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const isMobile = useIsMobile();

  // Check if this is a manual step that should show a Continue button
  const isManualStep = step.completionTrigger.type === 'manual' && onContinue;

  // Responsive width: on mobile, use viewport-aware sizing
  const mobileWidth = Math.min(window.innerWidth - 32, 320);
  const tooltipWidth = isMobile ? mobileWidth : (step.tooltipMaxWidth || TOOLTIP_MAX_WIDTH);
  const tooltipMinWidth = isMobile ? mobileWidth : (step.tooltipMaxWidth ? Math.min(step.tooltipMaxWidth, TOOLTIP_MIN_WIDTH) : TOOLTIP_MIN_WIDTH);

  // Track if position has been initialized to prevent drift animation on first render.
  // We only enable position transitions after the initial position is set.
  const [isPositionInitialized, setIsPositionInitialized] = useState(false);

  // --- Draggable tooltip support ---
  // Users can drag the tooltip header to reposition when it blocks content.
  // The drag offset is reset when the step changes so the tooltip returns to its calculated position.
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { x: clientX, y: clientY, offsetX: dragOffset.x, offsetY: dragOffset.y };

    const handleMove = (ev: MouseEvent | TouchEvent) => {
      if (!isDraggingRef.current) return;
      const cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
      setDragOffset({
        x: dragStartRef.current.offsetX + (cx - dragStartRef.current.x),
        y: dragStartRef.current.offsetY + (cy - dragStartRef.current.y),
      });
    };
    const handleEnd = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }, [dragOffset]);

  // Reset drag offset when step changes
  useEffect(() => {
    setDragOffset({ x: 0, y: 0 });
  }, [stepNumber]);

  // Measure actual tooltip height after render so calculatePosition uses real dimensions
  // instead of a hardcoded 200px estimate. This prevents tooltips with long content from
  // extending past the viewport edge.
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(200);

  // useLayoutEffect runs before paint, so the user never sees the wrong position.
  // Runs after every render but only updates state when height actually changes.
  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const actualHeight = tooltipRef.current.getBoundingClientRect().height;
      if (Math.abs(actualHeight - measuredHeight) > 5) {
        setMeasuredHeight(actualHeight);
      }
    }
  });

  // Reset measured height when step changes so we remeasure fresh content
  useEffect(() => {
    setMeasuredHeight(200);
  }, [stepNumber]);

  // Calculate position and arrow direction using measured height
  const { top, left: baseLeft, actualPosition } = useMemo(
    () => calculatePosition(targetRect, step.position, tooltipWidth, measuredHeight, isMobile),
    [targetRect, step.position, tooltipWidth, measuredHeight, isMobile]
  );

  // Apply optional horizontal offset (suppressed on mobile)
  const left = baseLeft + (isMobile ? 0 : (step.tooltipOffsetX || 0));

  // Mark position as initialized after the first position calculation
  // This runs after the initial render with the correct position
  useEffect(() => {
    // Small delay to ensure the initial position is applied without transition
    const timeoutId = setTimeout(() => {
      setIsPositionInitialized(true);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  // Reset initialization when step changes to prevent drift between steps
  useEffect(() => {
    setIsPositionInitialized(false);
    const timeoutId = setTimeout(() => {
      setIsPositionInitialized(true);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [stepNumber]);

  const arrowStyles = useMemo(() => getArrowStyles(actualPosition, isDark), [actualPosition, isDark]);
  const arrowClass = useMemo(() => getArrowClass(actualPosition), [actualPosition]);

  // Progress percentage
  const progressPercent = (stepNumber / totalSteps) * 100;

  // Estimate remaining time (approximately 30 seconds per step)
  const remainingSteps = totalSteps - stepNumber;
  const remainingMinutes = Math.ceil((remainingSteps * 30) / 60);
  const timeEstimate = remainingMinutes === 0
    ? 'Almost done!'
    : remainingMinutes === 1
      ? '~1 min left'
      : `~${remainingMinutes} min left`;

  const tooltipContent = (
    <div
      ref={tooltipRef}
      data-testid="tutorial-tooltip"
      className="tutorial-tooltip"
      role="tooltip"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: top + dragOffset.y,
        left: left + dragOffset.x,
        zIndex: 60,
        maxWidth: tooltipWidth,
        minWidth: tooltipMinWidth,
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        // Only apply position transition after initial position is set to prevent drift.
        // Suppress transition during drag for immediate responsiveness.
        transition: (isPositionInitialized && !isDraggingRef.current)
          ? 'transform 0.3s ease-in-out, top 0.3s ease-in-out, left 0.3s ease-in-out'
          : 'none',
        animation: 'fadeIn 0.2s ease-in-out',
      }}
    >
      <div className="bg-white dark:bg-cap-dark rounded-lg overflow-hidden">
        {/* Header with title, drag handle, and exit button */}
        <div
          className="flex items-start justify-between p-4 pb-2 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          role="toolbar"
          aria-label="Drag to reposition tooltip"
        >
          <div className="flex items-center gap-2 pr-2 min-w-0">
            {/* Drag grip indicator */}
            <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <circle cx="4" cy="3" r="1.5" />
              <circle cx="4" cy="8" r="1.5" />
              <circle cx="4" cy="13" r="1.5" />
              <circle cx="10" cy="3" r="1.5" />
              <circle cx="10" cy="8" r="1.5" />
              <circle cx="10" cy="13" r="1.5" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">{step.title}</h3>
          </div>
          <button
            type="button"
            onClick={onExit}
            aria-label="Exit tutorial"
            className="flex items-center justify-center min-w-[48px] min-h-[48px] md:min-w-0 md:min-h-0 text-gray-400 hover:text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded p-1 -mr-1 -mt-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Description - supports **bold** formatting for highlighting values */}
        <div className="px-4 pb-2 max-h-[40vh] md:max-h-[50vh] overflow-y-auto">
          <p className="text-gray-600 dark:text-gray-400 text-base md:text-sm whitespace-pre-line">{parseFormattedText(step.description)}</p>
        </div>

        {/* Action prompt - supports **bold** formatting */}
        <div className="px-4 pb-3">
          <p className="text-primary-600 text-sm font-medium">{parseFormattedText(step.actionPrompt)}</p>
        </div>

        {/* Progress indicator */}
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between text-sm md:text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span className="text-sm md:text-xs">Step {stepNumber} of {totalSteps} · {timeEstimate}</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <div
            className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={totalSteps}
            aria-valuenow={stepNumber}
            data-testid="progress-bar"
          >
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Continue/Finish button for manual (info-only) steps */}
        {isManualStep && (
          <div className="px-4 pb-3">
            {stepNumber === totalSteps ? (
              /* Final step - celebratory finish button */
              <button
                type="button"
                onClick={onContinue}
                className="w-full min-h-[48px] px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2"
              >
                Finish Tutorial <span role="img" aria-label="celebration">🎉</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={onContinue}
                className="w-full min-h-[48px] px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                Continue
              </button>
            )}
          </div>
        )}

        {/* Skip button - hidden on final step */}
        {stepNumber < totalSteps && (
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={onSkip}
              className="min-h-[48px] md:min-h-0 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 py-1 -ml-2"
            >
              Skip this step
            </button>
          </div>
        )}

        {/* Arrow pointing to target - hidden when tooltip has been dragged */}
        {(dragOffset.x === 0 && dragOffset.y === 0) && (
          <div
            data-testid="tooltip-arrow"
            className={`tooltip-arrow ${arrowClass}`}
            style={arrowStyles}
          />
        )}
      </div>
    </div>
  );

  // Render via portal for proper z-index layering
  return createPortal(tooltipContent, document.body);
};

export default TutorialTooltip;
